/**
 * StepTrigger — Wizard Step 2 (ONBOARDING_FLOW).
 *
 * MarthaChip group for trigger type (Schedule / On-Demand / From-Chat).
 * Selecting one reveals the relevant config:
 *  - schedule → cron-style picker stub
 *  - on-demand → nothing extra
 *  - from-chat → note that the source chat thread is linked
 */

import { Calendar, Zap, MessageSquare, Clock } from 'lucide-react';
import type { WorkStreamTriggerConfig, WorkStreamTriggerType, WorkStreamHolidayShift } from '../types';

const TRIGGERS: { type: WorkStreamTriggerType; label: string; description: string; icon: typeof Calendar }[] = [
  { type: 'schedule', label: 'Schedule', description: 'Run on a recurring cron schedule', icon: Calendar },
  { type: 'on-demand', label: 'On-Demand', description: 'Run whenever you tap play', icon: Zap },
  { type: 'from-chat', label: 'From-Chat', description: 'Triggered from a chat thread', icon: MessageSquare },
];

const HOLIDAY_SHIFT_OPTIONS: { value: WorkStreamHolidayShift; label: string }[] = [
  { value: 'previous', label: 'Previous business day' },
  { value: 'next', label: 'Next business day' },
  { value: 'skip', label: 'Run anyway' },
];

interface StepTriggerProps {
  value: WorkStreamTriggerConfig;
  onChange: (value: WorkStreamTriggerConfig) => void;
}

export function StepTrigger({ value, onChange }: StepTriggerProps) {
  return (
    <div>
      <span className="block text-sm font-medium text-text">How should it run?</span>
      <div role="radiogroup" aria-label="Trigger type" className="mt-2 grid grid-cols-1 gap-2">
        {TRIGGERS.map(t => {
          const selected = value.type === t.type;
          const Icon = t.icon;
          return (
            <button
              key={t.type}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange({ ...value, type: t.type })}
              className={`
                flex items-start gap-3 p-3 rounded-lg border text-left transition-all
                focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-400
                ${selected
                  ? 'border-primary-500 bg-primary-50 shadow-warm-sm'
                  : 'border-line bg-surface-card hover:border-primary-300 hover:bg-surface-subtle'}
              `}
            >
              <div className={`
                w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                ${selected ? 'bg-primary-100 text-primary-700' : 'bg-surface-subtle text-muted'}
              `}>
                <Icon size={14} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-text">{t.label}</div>
                <div className="text-xs text-muted">{t.description}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Conditional config per trigger type */}
      {value.type === 'schedule' && (
        <div className="mt-3 p-3 rounded-lg bg-surface-subtle border border-line">
          <label htmlFor="ws-cron" className="flex items-center gap-1.5 text-xs font-medium text-text">
            <Clock size={12} />
            Schedule (cron)
          </label>
          <input
            id="ws-cron"
            type="text"
            value={value.cron ?? ''}
            onChange={e => onChange({ ...value, cron: e.target.value })}
            placeholder="0 9 * * 1"
            className="mt-1.5 w-full px-3 py-1.5 rounded-md border border-line bg-surface-card text-xs font-mono text-text placeholder:text-subtle focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
          <p className="mt-1 text-[11px] text-subtle">Stub — full cron picker ships in a follow-up.</p>

          {/* Skip weekends — persisted as skip_weekends on the job_definition. */}
          <label
            htmlFor="ws-skip-weekends"
            className="mt-3 flex items-center justify-between gap-2 text-xs font-medium text-text"
          >
            <span className="flex items-center gap-1.5">
              <Clock size={12} />
              Skip weekends
            </span>
            <span className="flex items-center gap-2">
              <span className="text-[11px] text-subtle">{value.skipWeekends ? 'On' : 'Off'}</span>
              <button
                id="ws-skip-weekends"
                type="button"
                role="switch"
                aria-checked={value.skipWeekends ?? false}
                aria-label="Skip weekends"
                onClick={() => {
                  const skipWeekends = !value.skipWeekends;
                  // Turning skip-weekends off clears the (now-disabled) holiday
                  // shift choice so it doesn't linger on the create payload.
                  onChange(
                    skipWeekends
                      ? { ...value, skipWeekends }
                      : { ...value, skipWeekends, holidayShift: undefined },
                  );
                }}
                className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-400 ${
                  value.skipWeekends ? 'bg-primary-500' : 'bg-line'
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-surface-card shadow transition-transform ${
                    value.skipWeekends ? 'translate-x-3.5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </span>
          </label>

          {/* Holiday shift — contingent on skip weekends. Persisted via the
              holiday_calendar shift policy on the job_definition. Disabled
              (and its choice cleared) when skip weekends is off. */}
          <fieldset
            className="mt-3"
            disabled={!value.skipWeekends}
            aria-label="When it lands on a holiday"
          >
            <legend className="text-xs font-medium text-text">When it lands on a holiday</legend>
            <div role="radiogroup" aria-label="Holiday shift" className="mt-1.5 grid grid-cols-1 gap-1">
              {HOLIDAY_SHIFT_OPTIONS.map(opt => {
                const checked = value.holidayShift === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs transition-all ${
                      checked
                        ? 'border-primary-500 bg-primary-50 text-text'
                        : 'border-line bg-surface-card text-muted hover:border-primary-300'
                    } ${!value.skipWeekends ? 'opacity-50' : ''}`}
                  >
                    <input
                      type="radio"
                      name="ws-holiday-shift"
                      value={opt.value}
                      checked={checked}
                      disabled={!value.skipWeekends}
                      onChange={() => onChange({ ...value, holidayShift: opt.value })}
                      className="h-3 w-3 accent-primary-500"
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
            {!value.skipWeekends && (
              <p className="mt-1 text-[11px] text-subtle">
                Enable “Skip weekends” to choose how holidays are handled.
              </p>
            )}
          </fieldset>
        </div>
      )}
      {value.type === 'from-chat' && (
        <div className="mt-3 p-3 rounded-lg bg-surface-subtle border border-line">
          <p className="text-xs text-muted">
            This WorkStream will be linked to the chat thread it was promoted from.
            You can run it on-demand from the rail whenever the thread resurfaces.
          </p>
        </div>
      )}
    </div>
  );
}
